/**
 * Gestion du consentement côté loader.
 *
 * Stratégie V1 : strict opt-in manuel.
 *  - Le loader expose `halmya('consent', true|false)` via la queue.
 *  - Tant que `consent(true)` n'a pas été appelé, le loader ne charge PAS le core.
 *  - Auto-détection des CMP (Didomi, OneTrust, Axeptio, Cookiebot, Tarteaucitron,
 *    IAB TCF) sera ajoutée en V2 — pour l'instant l'intégrateur doit câbler
 *    son CMP à `halmya('consent', true)` manuellement (snippet documenté).
 *
 * Implémentation : on remplace la fonction-queue par une fonction wrapper qui :
 *  1. continue d'empiler tous les appels dans `q` (pour rejeu par le core)
 *  2. intercepte spécifiquement `['consent', true]` pour déclencher le boot
 */

import type { HalmyaQueue } from "../shared/types";

/**
 * Attend qu'un appel `halmya('consent', true)` arrive (existant dans la queue
 * ou ajouté ultérieurement) puis appelle `onGranted`.
 *
 * `onGranted` n'est invoqué qu'une seule fois, au premier `true` observé.
 */
export function waitForConsent(queue: unknown[][], onGranted: () => void): void {
  // 1. Scanner la queue existante : peut-être que consent(true) a déjà été
  //    poussé avant que le loader ne tourne.
  for (const args of queue) {
    if (isConsentGranted(args)) {
      onGranted();
      return;
    }
  }

  // 2. Wrapper la queue pour intercepter les futurs appels.
  let resolved = false;

  const wrapper = function (...args: unknown[]): void {
    queue.push(args);

    if (!resolved && isConsentGranted(args)) {
      resolved = true;
      onGranted();
    }
  } as HalmyaQueue;
  wrapper.q = queue;

  window.halmya = wrapper;
}

function isConsentGranted(args: unknown[]): boolean {
  return args.length >= 2 && args[0] === "consent" && args[1] === true;
}
